import { newPostBannerShownAtom, userInfoAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';

import { useForm } from 'app/hooks/form';
import { useNavigationOptions } from 'app/hooks/navigation';
import { useAddListingPictures } from 'app/network/listing';
import { useOneLoan } from 'app/network/loan';
import { useCreatePost } from 'app/network/post';
import type { NavigationScreen } from 'app/types/navigation';
import type { ImagePickerAsset } from 'expo-image-picker';
import { useAtom, useAtomValue } from 'jotai';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useSocialFeedFeature } from 'app/hooks/use-social-feed-feature';
import { PostComposer, PostComposerAction } from './components/post-composer';

interface NewPostFormValues {
  text: string;
  pictures: ImagePickerAsset[];
}

const newPostSchema: yup.ObjectSchema<NewPostFormValues> = yup.object({
  text: yup.string().trim().required(),
  pictures: yup.array().of(yup.mixed<ImagePickerAsset>().required()).defined(),
});

const NewPostScreenComponent: NavigationScreen<'new-post'> = ({ navigation, route }) => {
  const { loanId, listingId } = route.params;
  const { t } = useTranslation('posts');
  const { canUseSocialFeed, isSocialFeedLoading } = useSocialFeedFeature();

  useNavigationOptions(navigation, {
    headerTitle: () => (
      <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
        {t('newPost.title')}
      </Text>
    ),
  });

  const { mutate: addPictures } = useAddListingPictures(listingId);
  const { mutate: createPost, isPending } = useCreatePost({
    onSuccess: () => navigation.goBack(),
  });
  const { data: loan } = useOneLoan(loanId);
  const currentUser = useAtomValue(userInfoAtom);

  const {
    handleSubmit: handleFormSubmit,
    setValue,
    watch,
  } = useForm<NewPostFormValues>({
    defaultValues: { text: '', pictures: [] },
    schema: newPostSchema,
  });
  const text = watch('text');
  const hasText = text.trim().length > 0;
  const canCreatePost = Boolean(loan && loan.borrowerId === currentUser?.id && !loan.posts?.length);

  React.useEffect(() => {
    if (!isSocialFeedLoading && !canUseSocialFeed) navigation.goBack();
  }, [canUseSocialFeed, isSocialFeedLoading, navigation]);

  const handleSubmit = (values: NewPostFormValues) => {
    if (!canUseSocialFeed) return;

    if (values.pictures.length > 0) {
      addPictures(values.pictures);
    }

    createPost({
      loanId,
      text: values.text.trim(),
      pictures: values.pictures,
    });
  };

  const [newPostBannerShown, setNewPostBannerShown] = useAtom(newPostBannerShownAtom);

  if (isSocialFeedLoading || !canUseSocialFeed) return null;

  return (
    <Page
      accessory={
        canCreatePost ? (
          <PostComposerAction
            disabled={!hasText || isPending}
            label={t('newPost.submitButton')}
            loading={isPending}
            onPress={() => void handleFormSubmit(handleSubmit)()}
          />
        ) : undefined
      }
      keyboardShouldPersistTaps="handled"
      stretchToFill
    >
      {!loan || !currentUser ? (
        <Column alignItems="center" flex={1} justifyContent="center" padding="spacing.general.x8">
          <Text color="text.secondary">{t('newPost.loading')}</Text>
        </Column>
      ) : !canCreatePost ? (
        <Column
          alignItems="center"
          flex={1}
          gap="spacing.general.x3"
          justifyContent="center"
          padding="spacing.general.x8"
        >
          <Text fontWeight="700" textAlign="center" variant="bodyLarge">
            {t('newPost.unavailableTitle')}
          </Text>
          <Text color="text.secondary" textAlign="center" variant="bodyBase">
            {t('newPost.unavailableText')}
          </Text>
          <Button onPress={() => navigation.goBack()} variant="secondary">
            <Text fontWeight="700">{t('newPost.goBack')}</Text>
          </Button>
        </Column>
      ) : (
        <PostComposer
          loan={loan}
          onDismissPublicNotice={() => setNewPostBannerShown(true)}
          onPicturesChange={(pictures) => setValue('pictures', pictures)}
          onTextChange={(value) => setValue('text', value)}
          pictures={watch('pictures')}
          showPublicNotice={!newPostBannerShown}
          text={text}
        />
      )}
    </Page>
  );
};

export const NewPostScreen: NavigationScreen<'new-post'> = withErrorBoundary(NewPostScreenComponent);
